import { React, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Button, Alert, Dropdown } from 'react-bootstrap';
import ApiManager from '../../ApiManager';
import { handleApiError } from '../../utils/apiErrorHandler';
import ProjectTabs from './ProjectTabs';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from "react-router-dom";
import ConfirmationModal from '../Modals/ConfirmationModal';
import AddSectionsToProjectModal from '../Modals/AddSectionsToProjectModal';

const ProjectDetails = () => {
    const params = useParams();
    const projectId = params.projectId;
    const [project, setProject] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [isLoadingExcel, setIsLoadingExcel] = useState(false);
    const [isImportingExcel, setIsImportingExcel] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [isLoadingProjectSections, setIsLoadingProjectSections] = useState(false);
    const navigate = useNavigate();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showAddSectionsToProjectModal, setShowAddSectionsToProjectModal] = useState(false);
    const [archiveProjectHandler, setArchiveProjectHandler] = useState(false);
    const excelFileInputRef = useRef(null);

    useEffect(() => {
        if (isLoading) {
            ApiManager.getInstance().getProjectById(projectId).then((response) => {
                setProject(response);
            }).catch(handleApiError).finally(() => {
                setIsLoading(false);
            });
        }
    }, [isLoading, projectId]);

    useEffect(() => {
        if (archiveProjectHandler) {
            ApiManager.getInstance().deleteProject(project).then(() => {
                setArchiveProjectHandler(false);
                navigate('/projects/', { state: { isLoading: true, saveProject: false } });
            }).catch(handleApiError);
        }
    }, [archiveProjectHandler]); // eslint-disable-line react-hooks/exhaustive-deps

    const pdfExportClicked = () => {
        setIsLoadingPdf(true);
        ApiManager.getInstance().getProjectById(projectId).then((response) => {
            ApiManager.getInstance().getPdfById(projectId).then((result) => {
                setProject(response);
                var blob = new Blob([result.data], { type: 'application/pdf' });
                const pdfURL = URL.createObjectURL(blob);
                setTimeout(() => {
                    window.open(pdfURL, '_blank');
                })
                setIsLoadingPdf(false);
            }).catch((error) => { setIsLoadingPdf(false); handleApiError(error); });
        }).catch((error) => { setIsLoadingPdf(false); handleApiError(error); });
    }

    const excelExportClicked = () => {
        setIsLoadingExcel(true);
        ApiManager.getInstance().getExcelById(projectId).then((result) => {
            var blob = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const excelURL = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = excelURL;
            link.download = `${project.ClientName || project.Name || 'Project'}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(excelURL);
            setIsLoadingExcel(false);
        }).catch((error) => { setIsLoadingExcel(false); handleApiError(error); });
    }

    const ImportExcelClicked = () => {
        setImportResult(null);
        excelFileInputRef.current.click();
    }

    const handleExcelFileSelected = (e) => {
        const file = e.target.files[0];
        if (!file)
            return;

        setIsImportingExcel(true);
        setImportResult(null);
        const formData = new FormData();
        formData.append('File', file);

        ApiManager.getInstance().importExcelForProject(projectId, formData).then((result) => {
            if (excelFileInputRef.current) excelFileInputRef.current.value = "";
            setIsImportingExcel(false);
            const conflicts = result.data.Conflicts || [];
            setImportResult({
                variant: conflicts.length > 0 ? 'warning' : 'success',
                message: conflicts.length > 0
                    ? `Import complete: updated ${result.data.UpdatedCount} strategies. ${conflicts.length} field(s) could not be applied because someone else changed them first — see below.`
                    : `Import complete: updated ${result.data.UpdatedCount} strategies.`,
                conflicts
            });
            setIsLoading(true);
        }).catch((error) => {
            if (excelFileInputRef.current) excelFileInputRef.current.value = "";
            setIsImportingExcel(false);
            if (error.response && error.response.status === 400 && typeof error.response.data === 'string') {
                setImportResult({ variant: 'danger', message: error.response.data });
            } else {
                handleApiError(error);
            }
        });
    }

    const AddSectionClicked = () => {
        setShowAddSectionsToProjectModal(true);
    }

    //const ApplyDefaultNumbering = () => {
    //    ApiManager.getInstance().applyDefaultNumberingProject(projectId).then(() => {
    //        setIsLoading(true);
    //    });
    //}

    return (
        <>
            <AddSectionsToProjectModal
                setShowAddSectionsToProjectModal={setShowAddSectionsToProjectModal}
                showAddSectionsToProjectModal={showAddSectionsToProjectModal}
                projectName={project.Name}
                projectId={projectId}
                setIsLoadingProject={setIsLoading}
            />
            <ConfirmationModal
                type="Project"
                setYesHandler={setArchiveProjectHandler}
                showConfirmationModal={showConfirmationModal}
                setShowConfirmationModal={setShowConfirmationModal}
                Name={project.Name} />
            <Container>
                <input
                    type="file"
                    accept=".xlsx"
                    ref={excelFileInputRef}
                    onChange={handleExcelFileSelected}
                    style={{ display: 'none' }} />
                {isLoading || isLoadingPdf || isLoadingExcel || isImportingExcel || isLoadingProjectSections ?
                    <div style={{ justifyContent: "center", display: "flex", margin: "0 auto" }}>
                        <ThreeDots color="var(--pc-red)" />
                    </div>
                    :
                    <>
                        <h1>{project.Name}</h1>
                        {importResult &&
                            <Alert variant={importResult.variant} dismissible onClose={() => setImportResult(null)}>
                                <div>{importResult.message}</div>
                                {importResult.conflicts && importResult.conflicts.length > 0 &&
                                    <ul className="mt-2 mb-0">
                                        {importResult.conflicts.map((c, i) =>
                                            <li key={i}>
                                                <strong>{c.SheetName}</strong> — {c.StrategyLabel} — {c.FieldName}: you set "{c.AttemptedValue}", but it's currently "{c.CurrentValue}"
                                            </li>
                                        )}
                                    </ul>
                                }
                            </Alert>
                        }
                        <div className="d-flex justify-content-end gap-2 mb-3">
                            {!isLoadingPdf && !isLoadingExcel && !isImportingExcel &&
                                <>
                                    <Button size="sm" variant="outline-danger" onClick={() => setShowConfirmationModal(true)}>Archive Project</Button>
                                    <Dropdown>
                                        <Dropdown.Toggle size="sm" variant="outline-warning" id="export-import-dropdown">Export / Import</Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => pdfExportClicked()}>Export PDF</Dropdown.Item>
                                            <Dropdown.Item onClick={() => excelExportClicked()}>Export Excel</Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item onClick={() => ImportExcelClicked()}>Import Excel</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    <Button size="sm" variant="outline-primary" onClick={() => AddSectionClicked()}>Add Section</Button>
                                </>
                            }
                        </div>
                        <ProjectTabs setIsLoading={setIsLoading} project={project} setIsLoadingProjectSections={setIsLoadingProjectSections} />
                    </>}
            </Container>
        </>
    );
}
export default ProjectDetails;